import React, { useEffect, useRef } from 'react'
import { EyeIcon } from './Icons'
import { formatCompactNumber } from '../../../utils'

export const CustomTooltip = ({
  active,
  payload,
  showPolygonLines,
  singlePolygonData,
  showIndividualPolygonLines,
  polygonData,
  polygonColors,
  populationTrendsData,
  targetGroupName,
  targetGroupId,
  uiStore,
  singlePolygonId,
  hoveredPolygonIdRef,
  hoveredLineKey,
  lineSeriesDataKey,
  tooltipPointerInside,
  restrictTooltipToLineHover = false,
  rightSidebarStore,
  onTooltipEnter,
  onTooltipLeave
}) => {
  const previousPolygonIdRef = useRef(null)

  const isIndividualPolygonLine = showPolygonLines && showIndividualPolygonLines && polygonData && polygonData.length > 0
  const lineHoverAllowed = !restrictTooltipToLineHover
    ? true
    : isIndividualPolygonLine
      ? (tooltipPointerInside || !!hoveredLineKey)
      : (tooltipPointerInside || hoveredLineKey === lineSeriesDataKey)

  // Handle polygon highlighting when tooltip is active
  // This is more reliable than Line's onMouseEnter because tooltip activates when mouse is near the line
  useEffect(() => {
    const tooltipShouldShow = active && payload && payload.length && lineHoverAllowed
    if (tooltipShouldShow) {
      const isIndividualPolygonLine = showPolygonLines && showIndividualPolygonLines && polygonData && polygonData.length > 0
      const isSinglePolygon = singlePolygonData && !showIndividualPolygonLines

      if (isIndividualPolygonLine) {
        // Find the payload item that corresponds to the hovered line
        let payloadItem = null
        if (hoveredLineKey) {
          payloadItem = payload.find(item => item && item.dataKey === hoveredLineKey)
        }

        if (payloadItem) {
          const dataKey = payloadItem.dataKey || ''
          const polygonIdMatch = dataKey.match(/polygon_(\d+)/)

          if (polygonIdMatch && uiStore) {
            const polygonId = parseInt(polygonIdMatch[1], 10)
            const matchedPolygon = polygonData.find(p => p.id === polygonId)

            if (matchedPolygon && polygonId !== previousPolygonIdRef.current) {
              // Unhighlight previous polygon
              if (previousPolygonIdRef.current && hoveredPolygonIdRef) {
                uiStore.unhighlightPolygonFromChart(previousPolygonIdRef.current)
              }
              // Get the color for this polygon
              const polygonIndex = polygonData.findIndex(p => p.id === polygonId)
              const color = polygonColors[polygonIndex % polygonColors.length]
              // Highlight current polygon
              uiStore.highlightPolygonFromChart(polygonId, color)
              previousPolygonIdRef.current = polygonId
              if (hoveredPolygonIdRef) {
                hoveredPolygonIdRef.current = polygonId
              }
            }
          }
        } else {
          // If no specific line is hovered but tooltip is active (e.g. background hover), clear previous highlight
          if (previousPolygonIdRef.current && uiStore) {
            uiStore.unhighlightPolygonFromChart(previousPolygonIdRef.current)
            previousPolygonIdRef.current = null
            if (hoveredPolygonIdRef) {
              hoveredPolygonIdRef.current = null
            }
          }
        }
      } else if (isSinglePolygon && singlePolygonId && uiStore) {
        // Handle single polygon highlighting
        if (singlePolygonId !== previousPolygonIdRef.current) {
          if (previousPolygonIdRef.current && hoveredPolygonIdRef) {
            uiStore.unhighlightPolygonFromChart(previousPolygonIdRef.current)
          }
          uiStore.highlightPolygonFromChart(singlePolygonId, '#648E62')
          previousPolygonIdRef.current = singlePolygonId
          if (hoveredPolygonIdRef) {
            hoveredPolygonIdRef.current = singlePolygonId
          }
        }
      }
    } else {
      // Tooltip is not active, unhighlight if needed
      if (previousPolygonIdRef.current && uiStore) {
        uiStore.unhighlightPolygonFromChart(previousPolygonIdRef.current)
        previousPolygonIdRef.current = null
        if (hoveredPolygonIdRef) {
          hoveredPolygonIdRef.current = null
        }
      }
    }

    // Cleanup on unmount
    return () => {
      if (previousPolygonIdRef.current && uiStore) {
        uiStore.unhighlightPolygonFromChart(previousPolygonIdRef.current)
      }
    }
  }, [active, payload, showPolygonLines, showIndividualPolygonLines, polygonData, polygonColors, singlePolygonData, singlePolygonId, uiStore, hoveredPolygonIdRef, hoveredLineKey, lineHoverAllowed, restrictTooltipToLineHover])
  if (active && payload && payload.length) {
    if (!lineHoverAllowed) {
      return null
    }
    const data = payload[0].payload
    const isIndividualPolygonLine = showPolygonLines && showIndividualPolygonLines && polygonData.length > 0
    const isSinglePolygon = singlePolygonData && !showIndividualPolygonLines

    // For individual polygon lines, get the polygon info from the payload
    let shapeName = null
    let shapeColor = null
    let value = 0

    if (isIndividualPolygonLine && payload && payload.length > 0) {
      // Find the payload item that corresponds to the hovered line
      // Each line has a dataKey like "polygon_123", find the one with a valid value and polygon dataKey
      let payloadItem = null
      if (hoveredLineKey) {
        payloadItem = payload.find(item => item && item.dataKey === hoveredLineKey)
      }

      // If we are in individual lines mode but no specific line is hovered, don't show the tooltip
      if (!payloadItem) {
        return null
      }

      if (payloadItem) {
        // Extract polygon ID from dataKey (format: "polygon_123")
        const dataKey = payloadItem.dataKey || ''
        const polygonIdMatch = dataKey.match(/polygon_(\d+)/)

        if (polygonIdMatch) {
          const polygonId = parseInt(polygonIdMatch[1], 10)
          // Find the matching polygon in polygonData to get the correct name
          const matchedPolygon = polygonData.find(p => p.id === polygonId)

          if (matchedPolygon) {
            // Use the polygon's name from polygonData (this matches what's shown in the legend)
            shapeName = matchedPolygon.name || `Polygon ${polygonId}`
            // Get the color index to match the color used in rendering
            const polygonIndex = polygonData.findIndex(p => p.id === polygonId)
            shapeColor = polygonColors[polygonIndex % polygonColors.length]
          } else {
            // Fallback to payload name if polygon not found
            shapeName = payloadItem.name || 'Shape'
            shapeColor = payloadItem.color || '#648E62'
          }
        } else {
          // Fallback to payload name/color if dataKey doesn't match expected format
          shapeName = payloadItem.name || 'Shape'
          shapeColor = payloadItem.color || '#648E62'
        }
        value = payloadItem.value || 0
      }
    } else {
      // Target group or single polygon - use standard value
      value = data.population || data.average_population || data.aggregate_population || 0
    }

    return (
      <div className={`people-data-sidebar__tooltip-wrapper ${isIndividualPolygonLine ? 'people-data-sidebar__tooltip-wrapper--individual' : 'people-data-sidebar__tooltip-wrapper--target-group'}`}>
        {/* Invisible overlay to capture mouse events before they reach chart */}
        {/* <div
          onMouseEnter={(e) => {
            e.stopPropagation()
            if (e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation()
            }
            if (onTooltipEnter) onTooltipEnter()
          }}
          onMouseMove={(e) => {
            e.stopPropagation()
            if (e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation()
            }
          }}
          onMouseLeave={(e) => {
            e.stopPropagation()
            if (onTooltipLeave) onTooltipLeave()
          }}
          className="people-data-sidebar__tooltip-overlay"
        /> */}
        <div
          onMouseEnter={(e) => {
            e.stopPropagation()
            if (e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation()
            }
            if (onTooltipEnter) onTooltipEnter()
          }}
          onMouseMove={(e) => {
            e.stopPropagation()
            if (e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation()
            }
          }}
          onMouseLeave={(e) => {
            e.stopPropagation()
            if (onTooltipLeave) onTooltipLeave()
          }}
          onClick={(e) => {
            e.stopPropagation()
          }}
          onMouseDown={(e) => {
            e.stopPropagation()
          }}
          className={`people-data-sidebar__tooltip-container ${isIndividualPolygonLine ? 'people-data-sidebar__tooltip-container--individual' : ''}`}
        >
          {/* Shape Name and Value Section */}
          <div className='people-data-sidebar__tooltip-shape-value'>
            {isIndividualPolygonLine
              ? (
                // Show shape name with color
                <div className='people-data-sidebar__tooltip-shape-container'>
                  <div className='people-data-sidebar__tooltip-dash' style={{ backgroundColor: shapeColor }} />
                  <span className='people-data-sidebar__tooltip-shape-name' style={{ color: shapeColor }}>
                    {shapeName}
                  </span>
                </div>
                )
              : isSinglePolygon
                ? (
              // Show shape name for single polygon
                  <div className='people-data-sidebar__tooltip-shape-container'>
                    <div className='people-data-sidebar__tooltip-dash' />
                    <span className='people-data-sidebar__tooltip-shape-name'>
                      {targetGroupName || 'Polygon'}
                    </span>
                  </div>
                  )
                : (
              // Show year for target group
                  <div className='people-data-sidebar__tooltip-shape-container'>
                    <div className='people-data-sidebar__tooltip-dash' />
                    <span className='people-data-sidebar__tooltip-shape-name'>
                      {data.year || (populationTrendsData.length > 0 ? populationTrendsData[0].year : '2022')}
                    </span>
                  </div>
                  )}
            <span className='people-data-sidebar__tooltip-value'>
              {formatCompactNumber(value)}
            </span>
          </div>

          {!isIndividualPolygonLine && !isSinglePolygon && targetGroupId && rightSidebarStore
            ? (
              <button
                type='button'
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (e.nativeEvent) {
                    e.nativeEvent.stopImmediatePropagation()
                  }
                  rightSidebarStore.showShapeLines()
                }}
                onMouseEnter={(e) => {
                  e.stopPropagation()
                  if (e.nativeEvent) {
                    e.nativeEvent.stopImmediatePropagation()
                  }
                  if (onTooltipEnter) onTooltipEnter()
                }}
                onMouseMove={(e) => {
                  e.stopPropagation()
                  if (e.nativeEvent) {
                    e.nativeEvent.stopImmediatePropagation()
                  }
                }}
                onMouseLeave={(e) => {
                  e.stopPropagation()
                  if (onTooltipLeave) onTooltipLeave()
                }}
                className='people-data-sidebar__tooltip-button'
              >
                <EyeIcon />
                <span className='people-data-sidebar__tooltip-button-text'>
                  See individual shapes
                </span>
              </button>
              )
            : null}
        </div>
      </div>
    )
  }
  return null
}

export const CustomBarTooltip = ({
  active,
  payload,
  showPolygonLines,
  singlePolygonData,
  showIndividualPolygonLines,
  populationTrendsData
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    // Show "See Individual" button when not showing individual lines (works for both aggregate and average)
    const isTargetGroup = !showIndividualPolygonLines && !singlePolygonData
    const year = data.year || (populationTrendsData.length > 0 ? populationTrendsData[0].year : '2022')
    const value = data.population || 0

    return (
      <div className={`people-data-sidebar__tooltip-wrapper ${isTargetGroup ? 'people-data-sidebar__tooltip-wrapper--target-group' : 'people-data-sidebar__tooltip-wrapper--individual'}`}>
        {/* Invisible overlay to capture mouse events before they reach chart */}
        {/* <div
          onMouseEnter={(e) => {
            e.stopPropagation()
            if (e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation()
            }
          }}
          onMouseMove={(e) => {
            e.stopPropagation()
            if (e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation()
            }
          }}
          className="people-data-sidebar__tooltip-overlay"
        /> */}
        <div
          onMouseEnter={(e) => {
            e.stopPropagation()
            if (e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation()
            }
          }}
          onMouseMove={(e) => {
            e.stopPropagation()
            if (e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation()
            }
          }}
          onMouseLeave={(e) => {
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.stopPropagation()
          }}
          onMouseDown={(e) => {
            e.stopPropagation()
          }}
          className={`people-data-sidebar__tooltip-container people-data-sidebar__tooltip-container--bar ${isTargetGroup ? '' : 'people-data-sidebar__tooltip-container--individual'}`}
        >
          {/* Year and Value Section */}
          <div className='people-data-sidebar__tooltip-shape-value'>
            <div className='people-data-sidebar__tooltip-shape-container'>
              <div className='people-data-sidebar__tooltip-dash' />
              <span className='people-data-sidebar__tooltip-shape-name'>
                {year}
              </span>
            </div>
            <span className='people-data-sidebar__tooltip-value'>
              {formatCompactNumber(value)}
            </span>
          </div>
        </div>
      </div>
    )
  }
  return null
}

export const CustomStackedBarTooltip = ({
  active,
  payload,
  label,
  hoveredStackedKey
}) => {
  if (active && payload && payload.length) {
    const hoveredEntry = hoveredStackedKey
      ? payload.find((entry) => entry && entry.dataKey === hoveredStackedKey)
      : null

    if (!hoveredEntry) {
      return null
    }

    const year = label || hoveredEntry.payload.year
    const name = hoveredEntry.name || 'Shape'
    const value = hoveredEntry.value || 0
    const color = hoveredEntry.color || '#648E62'

    return (
      <div className='people-data-sidebar__stacked-tooltip'>
        {/* Year Header */}
        <div className='people-data-sidebar__stacked-tooltip-year'>
          {year}
        </div>

        {/* Single hovered series */}
        <div className='people-data-sidebar__stacked-tooltip-list'>
          <div className='people-data-sidebar__stacked-tooltip-item'>
            <div className='people-data-sidebar__tooltip-shape-container'>
              <div className='people-data-sidebar__tooltip-dash' style={{ backgroundColor: color }} />
              <span className='people-data-sidebar__stacked-tooltip-item-name'>
                {name}
              </span>
            </div>
            <span className='people-data-sidebar__stacked-tooltip-item-value'>
              {formatCompactNumber(value)}
            </span>
          </div>
        </div>
      </div>
    )
  }
  return null
}
